import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { composeUrl } from 'app/core/shared/helper/url.helper';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, catchError, tap, throwError } from 'rxjs';
import {
    AnnulusExceedMawopResponse,
    AnnulusPressureResponse,
    AprDashboardData,
    AprExceedMawopResponse,
    AprManagementStatistics,
    FieldData,
    WellStatistics,
} from '../model/apr-dashboard';

@Injectable({ providedIn: 'root' })
export class AprService {
    private _aprRecord: BehaviorSubject<any> = new BehaviorSubject(null);
    private _aprRecords: BehaviorSubject<any> = new BehaviorSubject(null);
    private _aprHistroicalRecord: BehaviorSubject<any> = new BehaviorSubject(
        null,
    );

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for data
     */

    get aprRecord$(): Observable<any> {
        return this._aprRecord.asObservable();
    }

    get aprRecords$(): Observable<any> {
        return this._aprRecords.asObservable();
    }

    get aprHistroicalRecord$(): Observable<any> {
        return this._aprHistroicalRecord.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    getAprRecords(params: any): Observable<any> {
        const url = composeUrl(
            '/v1/AnnulusPressureRecord/GetAnnulusPressureRecord',
            params,
        );

        return this._httpClient.get<any>(url);
    }

    getAprRecord(wellStirngId): Observable<any> {
        return this._httpClient
            .get<any>(
                `${environment.apiURL}/${environment.apiVersion}/AnnulusPressureRecord/GetAnnulusPressureRecord/${wellStirngId}`,
            )
            .pipe(
                tap((response: any) => {
                    this._aprRecord.next(response);
                }),
            );
    }

    getHistoricalData(aprId, parameter) {
        const url = composeUrl(
            `/v1/AnnulusPressureRecord/GetAprHistoricalRecord/${aprId}`,
            parameter,
        );

        return this._httpClient.get<any>(url).pipe(
            tap((response: any) => {
                this._aprHistroicalRecord.next(response);
            }),
        );
    }

    getHistoricalPiData(parameter) {
        const url = composeUrl(
            `/v1/AnnulusPressureRecord/GetAprPiHistoricalRecord`,
            parameter,
        );

        return this._httpClient.get<any>(url).pipe(
            tap((response: any) => {
                this._aprHistroicalRecord.next(response);
            }),
        );
    }

    getAprPiRecords(params: any): Observable<any> {
        const url = composeUrl(
            '/v1/AnnulusPressureRecord/GetAprPiRecord',
            params,
        );

        return this._httpClient.get<any>(url);
    }

    getAprPiRecordById(piId): Observable<any> {
        return this._httpClient
            .get<any>(
                `${environment.apiURL}/${environment.apiVersion}/AnnulusPressureRecord/GetAprPiRecord/${piId}`,
            )
            .pipe(
                tap((response: any) => {
                    this._aprRecord.next(response);
                }),
            );
    }

    downloadOverallApr(activity): Observable<any> {
        let params = new HttpParams().set('activity', activity);

        return this._httpClient
            .get(
                `${environment.apiURL}/${environment.apiVersion}/AnnulusPressureRecord/DownloadOverallApr`,
                {
                    params: params,
                    responseType: 'blob',
                },
            )
            .pipe(
                tap((response: any) => {
                    return response;
                }),
            );
    }

    downloadOverallAprPi(): Observable<any> {
        return this._httpClient
            .get(
                `${environment.apiURL}/${environment.apiVersion}/AnnulusPressureRecord/DownloadOverallAprPi`,
                {
                    responseType: 'blob',
                },
            )
            .pipe(
                tap((response: any) => {
                    return response;
                }),
            );
    }

    downloadHistoricalApr(aprId): Observable<any> {
        return this._httpClient
            .get(
                `${environment.apiURL}/${environment.apiVersion}/AnnulusPressureRecord/DownloadHistoricalApr/${aprId}`,
                {
                    responseType: 'blob',
                },
            )
            .pipe(
                tap((response: any) => {
                    return response;
                }),
            );
    }

    createAprecords(payload) {
        return this._httpClient
            .post<any>(
                `${environment.apiURL}/${environment.apiVersion}/AnnulusPressureRecord/CreateAnnulusPressureRecord`,
                payload,
            )
            .pipe(
                tap((response: any) => {
                    return response;
                }),
            );
    }

    updateAprecord(aprId, payload) {
        return this._httpClient
            .put<any>(
                `${environment.apiURL}/${environment.apiVersion}/AnnulusPressureRecord/UpdateAnnulusPressureRecord/${aprId}`,
                payload,
            )
            .pipe(
                tap((response: any) => {
                    return response;
                }),
            );
    }

    getAprPiChart(): Observable<AprDashboardData<WellStatistics>> {
        const url = composeUrl('/v1/WIM/DashboardWIM/apr/summary');

        return this._httpClient.get<AprDashboardData<WellStatistics>>(url);
    }

    getAprManagementPiChart(): Observable<AprManagementStatistics> {
        const url = composeUrl('/v1/WIM/DashboardWIM/apr/management');

        return this._httpClient.get<AprManagementStatistics>(url);
    }

    getAprPlatforms(): Observable<FieldData> {
        const url = composeUrl('/v1/WIM/DashboardWIM/apr/platforms');

        return this._httpClient.get<FieldData>(url).pipe(
            catchError((error) => {
                console.error('[AprService] Failed to get platforms', error);
                return throwError(() => error);
            }),
        );
    }

    getAprPlatformSummary(
        platformId: string,
    ): Observable<AprDashboardData<WellStatistics>> {
        const url = composeUrl(
            `/v1/WIM/DashboardWIM/apr/platforms/${platformId}`,
        );

        return this._httpClient.get<AprDashboardData<WellStatistics>>(url);
    }

    getAprPlatformAnnulusPressure(
        platformId: string,
    ): Observable<AnnulusPressureResponse> {
        const url = composeUrl(
            `/v1/WIM/DashboardWIM/apr/platforms/summary/${platformId}`,
        );

        return this._httpClient.get<AnnulusPressureResponse>(url).pipe(
            catchError((error) => {
                console.error(
                    '[AprService] Failed to get annulus pressure data',
                    error,
                );
                return throwError(() => error);
            }),
        );
    }

    getAprWellStringAnnulusPressure(
        wellStringId: string,
        year: number | null = null,
    ): Observable<AnnulusPressureResponse> {
        const params = year === null ? undefined : { year };
        const url = composeUrl(
            `/v1/WIM/DashboardWIM/apr/wells/summary/${wellStringId}`,
            params,
        );

        return this._httpClient.get<AnnulusPressureResponse>(url).pipe(
            catchError((error) => {
                console.error(
                    '[AprService] Failed to get annulus pressure data',
                    error,
                );
                return throwError(() => error);
            }),
        );
    }

    getAprExceedMawopCount(): Observable<AprExceedMawopResponse> {
        const url = composeUrl(`/v1/WIM/DashboardWIM/apr/exceed-mawop/count`);

        return this._httpClient.get<AprExceedMawopResponse>(url).pipe(
            catchError((error) => {
                console.error(
                    '[AprService] Failed to get exceed MAWOP count',
                    error,
                );
                return throwError(() => error);
            }),
        );
    }

    getAprAnnulusExceedMawopCount(): Observable<AnnulusExceedMawopResponse> {
        const url = composeUrl(
            `/v1/WIM/DashboardWIM/apr/annulus-exceed-mawop/count`,
        );

        return this._httpClient.get<AnnulusExceedMawopResponse>(url).pipe(
            catchError((error) => {
                console.error(
                    '[AprService] Failed to get annulus exceed MAWOP count',
                    error,
                );
                return throwError(() => error);
            }),
        );
    }
}
